<script>
import api from "@/helpers/api-rest/api.js";
import store from "@/state/store";
import qs from "qs";
import { required, email, helpers } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import { errorMessages } from "@/helpers/validations/errorMessages";
import { notifyValidationFailedAndFocusFirstInvalid } from "@/helpers/validations/vuelidateFormFeedback";
import CenterModal from "@/components/modals/center-modal.vue";
import AppTextareaAi from "@/components/forms/AppTextareaAi.vue";

const isPdf = (value) => {
  return typeof value === "string" && value.toLowerCase().endsWith(".pdf");
};

// Validator: Only letters (including Spanish characters)
const onlyLetters = helpers.regex("onlyLetters", /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/);


export default {
  components: {
    CenterModal,
    AppTextareaAi,
  },
  props: {
    id: { type: String, default: "ModalAccessionCenter" },
    essayId: { type: [Number, String] },
    centerId: { type: Number },
    accesion_center: {
      type: Object,
      default: () => {
        return {
          id: null,
          center_id: null,
          document_name: null,
          accession_center_professorates: [],
        };
      },
    },
  },
  data() {
    return {
      isModalOpen: false,
      dataUser: [],
      selectOptionsTrackingStatus: [],
      selectOptionsActions: [],
      collapseOpen: [],
      loading: false,
      selectOptionsCenter: [],
      dataEssay: {},
      errorMessages,
      validateFile: false,
      downloadingAnexoIV: false,
      uploadingFile: false,
      accessionCenter: null, // Store center data
      modalCenterId: null, // ID of center being edited (null for new)
      genderOptions: [
        { value: 'male', label: 'Hombre' },
        { value: 'female', label: 'Mujer' },
        { value: 'other', label: 'Otro' },
      ],
      ageRangeOptions: [
        { value: '0-17', label: '0-17' },
        { value: '18-29', label: '18-29' },
        { value: '30-54', label: '30-54' },
        { value: '55-plus', label: '55 y más' }
      ],
    };
  },
  computed: {
    modalTitle() {
      return this.accesion_center?.id ? "Editar adhesión" : "Añadir adhesión";
    },
    submitButtonText() {
      return this.accesion_center?.id ? "Guardar" : "Añadir";
    },
    // Computed props for the CenterModal instance
    centerModalProps() {
      return {
        essay_id: this.essayId,
        center_type_id: 2, // Accession center type
        id: this.modalCenterId,
      };
    },
  },
  created() {
    // Removed loadSelectOptionsCenter - no longer needed
  },
  validations() {
    return {
      accesion_center: {
        reason: { required },
        center_id: {
          required, // Always required now
        },
        accession_center_professorates: {
          $each: {
            name: { required, onlyLetters },
            last_name: { required, onlyLetters },
            gender: { required },
            department: { required },
            dni: { required },
            email: { required, email },
            age_range: {}, // Optional field
          },
        },
        document_name: this.validateFile ? { required, isPdf } : {},
      },
    };
  },
  methods: {
    formatDate(d = new Date()) {
      return `${String(d.getDate()).padStart(2, "0")}${String(
        d.getMonth() + 1
      ).padStart(2, "0")}${d.getFullYear()}${String(d.getHours()).padStart(
        2,
        "0"
      )}${String(d.getMinutes()).padStart(2, "0")}`;
    },
    loadDataEssay() {
      const apiEndpoint = `/api/v1/essays/${this.essayId}`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          const data = response?.data?.data;
          this.dataEssay = data;
        })
        .catch((error) => {
          console.error("Error loading data:", error);
        });
    },
    loadAccessionCenter() {
      const centerId = this.accesion_center?.center_id;
      if (!centerId) {
        this.accessionCenter = null;
        return;
      }
      
      const apiEndpoint = `/api/v1/centers/${centerId}`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          this.accessionCenter = response?.data?.data;
        })
        .catch((error) => {
          console.error("Error loading accession center:", error);
          this.accessionCenter = null;
        });
    },
    openCenterModal() {
      // Set state for accession center
      const centerId = this.accesion_center?.center_id || 
                       (this.accessionCenter && this.accessionCenter.id) || 
                       null;
      this.modalCenterId = centerId;
      this.$bvModal.show("ModalAccessionCenterModal");
    },
    handleCenterSuccess(center) {
      // Update center_id and store center data
      // Ensure center_id is a number
      this.accesion_center.center_id = center.id ? Number(center.id) : null;
      this.accessionCenter = center;
      // Reset validation for center_id to clear any previous errors
      this.$nextTick(() => {
        if (this.$v.accesion_center?.center_id) {
          this.$v.accesion_center.center_id.$reset();
          // Touch again to re-validate with the new value
          this.$v.accesion_center.center_id.$touch();
        }
      });
      // Reset modal state
      this.modalCenterId = null;
    },
    showElement(
      roleId = [],
      show = true,
      showRechazado = true,
      showRenuncia = true
    ) {
      const role = store.getters["auth/role"];
      if (roleId.includes(role.id) && show && showRechazado && showRenuncia) {
        return true;
      }
      return false;
    },
    onFileChange(event) {
      this.file = event.target.files[0];

      if (this.file.type !== "application/pdf") {
        this.accesion_center.document_name = "";
        this.$v.accesion_center.document_name.$touch();
        return;
      }

      this.$v.accesion_center.document_name.$reset();

      this.accesion_center.document_name =
        this.formatDate() + "-" + this.file?.name;
      this.uploadingFile = true;
      this.uploadFile(this.file, "accession")
        .then((response) => {
          const file = response?.data?.data;
          this.accesion_center.document_path = file.path;
        })
        .catch((error) => {
          console.error("Error al cargar el archivo:", error);
          // Reset file name and path on error
          this.accesion_center.document_name = null;
          this.accesion_center.document_path = null;
        })
        .finally(() => {
          this.uploadingFile = false;
        });
    },
    uploadFile(file, folder) {
      const formData = new FormData();
      formData.set("file", file);
      formData.append("path", folder);
      const config = { headers: { "content-type": "multipart/form-data" } };

      const apiEndpoint = `/api/v1/upload-file`;
      return api
        .post({ url: apiEndpoint, data: formData, config })
        .catch((error) => {
          this.loading = false;
          console.error("Error upload file:", error);

          // Extract error message from response
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Error al subir el archivo. Por favor, inténtelo de nuevo.";

          Swal.fire({
            icon: "error",
            title: "Error al subir el archivo",
            html: errorMessage,
            confirmButtonColor: "#34c38f",
            confirmButtonText: "Aceptar",
          });
        });
    },
    async downloadFileAnexoIV() {
      if (this.downloadingAnexoIV) return;
      
      this.downloadingAnexoIV = true;
      try {
        const apiEndpoint = `/api/v1/anexo4`;
        const data = {
          accesion_center: this.accesion_center.accession_center_professorates,
          essay_id: this.essayId,
          accession: true,
          center_id: this.accesion_center.center_id,
        };

        const config = {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        };

        const response = await api.post({
          url: apiEndpoint,
          data: data,
          baseURL: process.env.VUE_APP_API_HOST,
          config: config,
        });

        const blob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "Documento adhesión centro.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error al descargar el Documento:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Hubo un error al descargar el Documento. Por favor, inténtalo de nuevo.",
        });
      } finally {
        this.downloadingAnexoIV = false;
      }
    },
    downloadFiles(fileName, folderID) {
      const params = {
        fileName: fileName,
        folderID: folderID,
      };

      const queryString = qs.stringify(params);
      const baseUrl = process.env.VUE_APP_API_HOST;
      const url = `${baseUrl}/download-drive?${queryString}`;
      window.open(url, "_blank");
    },
    logValidationErrors() {
      const errors = [];

      const checkErrors = (validation, path = "") => {
        for (const field in validation) {
          if (validation[field]?.$invalid) {
            errors.push(`${path}${field} es inválido`);
          }

          // Si es un objeto con más validaciones dentro, recursión
          if (
            typeof validation[field] === "object" &&
            validation[field] !== null
          ) {
            checkErrors(validation[field], `${path}${field}.`);
          }
        }
      };

      checkErrors(this.$v.accesion_center);

      console.log("Errores de validación:", errors);
    },
    createAccessionCenter() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.logValidationErrors();
        notifyValidationFailedAndFocusFirstInvalid(this);
        return;
      }
      this.loading = true;
      const apiEndpoint = `/api/v1/accession-centers`;
      this.accesion_center.essay_id = this.essayId;
      api
        .post({ url: apiEndpoint, data: this.accesion_center })
        .then((response) => {
          this.loading = false;
          const newAccession = response?.data?.data;
          this.$emit("success", newAccession);
          this.$bvModal.hide(this.id);
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al crear adhesion de empresa:", error);
        });
    },

    updateAccessionCenter() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        notifyValidationFailedAndFocusFirstInvalid(this);
        return;
      }
      this.loading = true;
      const apiEndpoint = `/api/v1/accession-centers`;
      api
        .put({
          url: apiEndpoint,
          data: this.accesion_center,
          id: this.accesion_center.id,
        })
        .then((response) => {
          this.loading = false;
          const updatedAccession = response?.data?.data;
          this.$emit("success", updatedAccession);
          this.$bvModal.hide(this.id);
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al editar adhesion de empresa:", error);
        });
    },
    changeStatusAccession(status) {
      this.accesion_center.status = status;
      this.submitForm();
    },
    submitForm() {
      // Prevent form submission if file is still uploading
      if (this.uploadingFile) {
        Swal.fire({
          icon: "warning",
          title: "Espera",
          text: "Por favor, espera a que termine la subida del documento antes de guardar.",
          confirmButtonColor: "#34c38f",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      if (this.accesion_center?.id) {
        return this.updateAccessionCenter();
      }
      return this.createAccessionCenter();
    },
    cancelForm() {
      this.$bvModal.hide(this.id);
    },
    addRowProfessorate() {
      this.accesion_center.accession_center_professorates.push({
        id: "",
        name: "",
        last_name: "",
        gender: null,
        department: "",
        dni: "",
        email: "",
        age_range: null,
        accesion_center_id: this.accesion_center.id,
      });
    },
    deleteRowProfessorate(key) {
      this.accesion_center?.accession_center_professorates.splice(key, 1);
    },
    loadData() {
      this.$v.$reset();
      this.uploadingFile = false;
      this.loadAccessionCenter();
    },
    saveAccession(status_user, validateFile = false) {
      // Prevent form submission if file is still uploading
      if (this.uploadingFile) {
        Swal.fire({
          icon: "warning",
          title: "Espera",
          text: "Por favor, espera a que termine la subida del documento antes de guardar.",
          confirmButtonColor: "#34c38f",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      this.validateFile = validateFile;
      this.$v.$reset();
      this.accesion_center.status_user = status_user;
      this.submitForm();
    },
    addAccession(status_user) {
      // Alias method that calls saveAccession with validateFile = true
      this.saveAccession(status_user, true);
    },
  },
  mounted() {
    this.loadDataEssay();
  },
};
</script>
<template>
  <div>
    <CenterModal
      :essay_id="centerModalProps.essay_id"
      :center_type_id="centerModalProps.center_type_id"
      :id="centerModalProps.id"
      modal-id="ModalAccessionCenterModal"
      @success="handleCenterSuccess"
    />
    <b-modal
      :id="id"
      :title="modalTitle"
      @shown="loadData"
      scrollable
      size="extra-xl"
      no-close-on-backdrop
      no-close-on-esc
    >
      <template #default="{}">
        <form @submit.prevent="submitForm" enctype="multipart/form-data">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-lg-12">
                  <h6>Justificación <span style="color: red">*</span></h6>
                  <div
                    v-if="
                      accesion_center?.essay?.essay_status_id == 7 ||
                      accesion_center?.essay?.essay_status_id == 4 ||
                      accesion_center?.essay?.essay_status_id == 6
                    "
                  >
                    <p class="text-muted">
                      {{ accesion_center?.reason }}
                    </p>
                  </div>
                  <!-- <p v-if="showElement([1])" class="text-muted">
                      {{ accesion_center?.reason }}
                  </p> -->
                  <div v-else class="form-group">
                    <app-textarea-ai
                      v-model="accesion_center.reason"
                      :enable-ai="true"
                      :max-chars="5000"
                      :rows="3"
                      placeholder="Introduce justificación"
                      :textarea-class="{
                        'form-control': true,
                        'is-invalid': $v.accesion_center.reason.$error,
                      }"
                    ></app-textarea-ai>
                    <small class="text-muted"
                      >{{ accesion_center?.reason?.length }}/5000</small
                    >
                    <div
                      v-if="$v.accesion_center.reason.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_center.reason.required">
                        {{ errorMessages.required }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-12">
                  <h4 class="card-title">Centro</h4>
                  <div class="form-group col-12 mt-4">
                    <div class="row">
                      <div class="form-group col-12">
                        <label
                          >Centro
                          <span style="color: red">*</span></label
                        >
                        <!-- Show center info if exists, otherwise show add button -->
                        <div v-if="accessionCenter && accessionCenter.id">
                          <b-card class="mb-3">
                            <div class="d-flex justify-content-between align-items-start">
                              <div class="flex-grow-1">
                                <h5 class="mb-2">{{ accessionCenter.name }}</h5>
                                <p class="mb-1">
                                  <strong>Código:</strong> {{ accessionCenter.center_code }}
                                </p>
                                <p class="mb-1">
                                  <strong>Dirección:</strong> {{ accessionCenter.address }}, {{ accessionCenter.city }}, {{ accessionCenter.province }}
                                </p>
                                <p class="mb-1">
                                  <strong>Email:</strong> {{ accessionCenter.email }}
                                </p>
                                <p class="mb-1">
                                  <strong>Teléfono:</strong> {{ accessionCenter.phone }}
                                </p>
                                <p class="mb-0">
                                  <strong>Director:</strong> {{ accessionCenter.director }}
                                </p>
                              </div>
                              <div>
                                <b-button
                                  variant="primary"
                                  size="sm"
                                  @click="openCenterModal"
                                  class="ml-2"
                                  v-if="
                                    !accesion_center?.essay?.essay_status_id ||
                                    ![4, 6, 7].includes(accesion_center?.essay?.essay_status_id)
                                  "
                                >
                                  <i class="mdi mdi-pencil"></i> Editar
                                </b-button>
                              </div>
                            </div>
                          </b-card>
                        </div>
                        <div v-else>
                          <b-button
                            variant="success"
                            @click="openCenterModal"
                            :class="{
                              'is-invalid':
                                $v.accesion_center.center_id.$error,
                            }"
                            v-if="
                              !accesion_center?.essay?.essay_status_id ||
                              ![4, 6, 7].includes(accesion_center?.essay?.essay_status_id)
                            "
                          >
                            <i class="mdi mdi-plus-circle"></i> Añadir Centro
                          </b-button>
                          <div
                            v-if="$v.accesion_center.center_id.$error"
                            class="invalid-feedback d-block"
                          >
                            <div v-if="!$v.accesion_center.center_id.required">
                              {{ errorMessages.required }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-12 mt-3">
                  <label>Profesorado participante</label>
                  <table class="table table-bordered table-sticky-header">
                    <thead>
                      <tr>
                        <th scope="col">
                          Nombre <span style="color: red">*</span>
                        </th>
                        <th scope="col">
                          Apellido <span style="color: red">*</span>
                        </th>
                        <th scope="col">
                          Género <span style="color: red">*</span>
                        </th>
                        <th scope="col">
                          Departamento <span style="color: red">*</span>
                        </th>
                        <th scope="col">DNI <span style="color: red">*</span></th>
                        <th scope="col">
                          Email <span style="color: red">*</span>
                        </th>
                        <th scope="col">Rango de edad</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(
                          professorate, i
                        ) in accesion_center?.accession_center_professorates"
                        :key="i"
                        class=""
                        :set="
                          (vprofessorate =
                            $v.accesion_center.accession_center_professorates
                              .$each[i])
                        "
                      >
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.name }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <input
                              type="text"
                              v-model="professorate.name"
                              class="form-control"
                              placeholder="Nombre"
                              maxlength="200"
                              :class="{
                                'is-invalid': vprofessorate.name.$error,
                              }"
                            />
                            <small class="text-muted"
                              >{{ professorate?.name?.length }}/200</small
                            >
                            <div
                              v-if="vprofessorate.name.$error"
                              class="invalid-feedback"
                            >
                              <div v-if="!vprofessorate.name.required">
                                {{ errorMessages.required }}
                              </div>
                              <div
                                v-else-if="!vprofessorate.name.onlyLetters"
                              >
                                {{ errorMessages.onlyLetters }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.last_name }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <input
                              type="text"
                              v-model="professorate.last_name"
                              class="form-control"
                              placeholder="Apellido"
                              maxlength="200"
                              :class="{
                                'is-invalid': vprofessorate.last_name?.$error,
                              }"
                            />
                            <small class="text-muted"
                              >{{ professorate?.last_name?.length }}/200</small
                            >
                            <div
                              v-if="vprofessorate.last_name?.$error"
                              class="invalid-feedback"
                            >
                              <div v-if="!vprofessorate.last_name?.required">
                                {{ errorMessages.required }}
                              </div>
                              <div
                                v-else-if="!vprofessorate.last_name?.onlyLetters"
                              >
                                {{ errorMessages.onlyLetters }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.gender === 'male' ? 'Hombre' : professorate?.gender === 'female' ? 'Mujer' : '' }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <select
                              v-model="professorate.gender"
                              class="form-control"
                              :class="{
                                'is-invalid': vprofessorate.gender?.$error,
                              }"
                            >
                              <option :value="null">Seleccionar género</option>
                              <option
                                v-for="option in genderOptions"
                                :key="option.value"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                            <div
                              v-if="vprofessorate.gender?.$error"
                              class="invalid-feedback"
                            >
                              <div v-if="!vprofessorate.gender?.required">
                                {{ errorMessages.required }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.department }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <input
                              type="text"
                              v-model="professorate.department"
                              class="form-control"
                              placeholder="Departamento"
                              maxlength="200"
                              :class="{
                                'is-invalid': vprofessorate.department.$error,
                              }"
                            />
                            <small class="text-muted"
                              >{{ professorate?.department?.length }}/200</small
                            >
                            <div
                              v-if="vprofessorate.department.$error"
                              class="invalid-feedback"
                            >
                              <div v-if="!vprofessorate.department.required">
                                {{ errorMessages.required }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.dni }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <input
                              type="text"
                              v-model="professorate.dni"
                              class="form-control"
                              placeholder="DNI"
                              maxlength="10"
                              :class="{
                                'is-invalid': vprofessorate.dni.$error,
                              }"
                            />
                            <small class="text-muted"
                              >{{ professorate?.dni?.length }}/10</small
                            >
                            <div
                              v-if="vprofessorate.dni.$error"
                              class="invalid-feedback"
                            >
                              <div v-if="!vprofessorate.dni.required">
                                {{ errorMessages.required }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.email }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <input
                              type="text"
                              v-model="professorate.email"
                              class="form-control"
                              placeholder="Email"
                              :class="{
                                'is-invalid': vprofessorate.email.$error,
                              }"
                            />
                            <div
                              v-if="vprofessorate.email.$error"
                              class="invalid-feedback"
                            >
                              <div v-if="!vprofessorate.email.required">
                                {{ errorMessages.required }}
                              </div>
                              <span v-else-if="!vprofessorate.email.email">{{
                                errorMessages.email
                              }}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            v-if="
                              accesion_center?.essay?.essay_status_id == 7 ||
                              accesion_center?.essay?.essay_status_id == 4 ||
                              accesion_center?.essay?.essay_status_id == 6
                            "
                          >
                            <p class="text-muted">
                              {{ professorate?.age_range ? ageRangeOptions.find(opt => opt.value === professorate.age_range)?.label : '' }}
                            </p>
                          </div>
                          <div v-else class="form-group">
                            <select
                              v-model="professorate.age_range"
                              class="form-control"
                            >
                              <option :value="null">Seleccionar rango de edad</option>
                              <option
                                v-for="option in ageRangeOptions"
                                :key="option.value"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </div>
                        </td>
                        <td
                          v-if="
                            showElement(
                              [3],
                              ![4, 6, 7].includes(accesion_center?.essay?.essay_status_id)
                            )
                          "
                          class="text-center"
                        >
                          <a
                            href="javascript:void(0);"
                            class="text-danger"
                            v-b-tooltip.hover
                            title="Eliminar"
                            @click="deleteRowProfessorate(i)"
                          >
                            <i class="mdi mdi-trash-can font-size-18"></i>
                          </a>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot
                      v-if="
                        showElement(
                          [3],
                          ![4, 6, 7].includes(accesion_center?.essay?.essay_status_id),
                        )
                      "
                    >
                      <tr>
                        <td colspan="8" class="text-right">
                          <a
                            href="javascript:void(0);"
                            class="text-success"
                            @click="addRowProfessorate()"
                          >
                            <i class="mdi mdi-plus-circle font-size-18"></i>
                            Agregar profesorado
                          </a>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div v-if="showElement([3])" class="row mt-4">
                <div class="col-lg-12">
                  <b-alert show variant="warning">
                    <strong>Observaciones</strong><br />
                    <ul>
                      <li>
                        Antes de adjuntar el <strong>Documento</strong>, primero
                        debes generarlo
                        <a
                          href="javascript:void(0);"
                          @click.prevent="downloadFileAnexoIV()"
                          :class="{ 
                            'text-muted': downloadingAnexoIV
                          }"
                          :style="{ 
                            cursor: downloadingAnexoIV ? 'not-allowed' : 'pointer',
                            pointerEvents: downloadingAnexoIV ? 'none' : 'auto'
                          }"
                        >
                          <span
                            v-if="downloadingAnexoIV"
                            class="spinner-border spinner-border-sm mr-1"
                            role="status"
                            aria-hidden="true"
                            style="width: 0.875rem; height: 0.875rem;"
                          ></span>
                          {{ downloadingAnexoIV ? 'descargando' : 'aquí' }}
                        </a>
                      </li>
                      <li>
                        Si has completado los campos anteriores, también
                        aparecerán en el <strong>Documento</strong>
                      </li>
                    </ul>
                  </b-alert>
                </div>
                <div
                  v-if="
                    showElement(
                      [3],
                      ![4, 6, 7].includes(accesion_center?.essay?.essay_status_id)
                    )
                  "
                  class="col-lg-6 mt-3"
                >
                  <h6>Adjuntar Documento <span class="text-danger">*</span></h6>
                  <div class="form-group">
                    <div class="custom-file">
                      <input
                        id="inputFile"
                        type="file"
                        name="file"
                        class="custom-file-input"
                        @change="onFileChange"
                        :disabled="uploadingFile"
                        :class="{
                          'is-invalid': $v.accesion_center.document_name.$error,
                        }"
                        accept="application/pdf"
                      />
                      <label class="custom-file-label" for="inputFile">{{
                        accesion_center.document_name || "Seleccione un archivo"
                      }}</label>
                      <div
                        v-if="$v.accesion_center.document_name.$error"
                        class="invalid-feedback"
                      >
                        <div v-if="!$v.accesion_center.document_name.isPdf">
                          Solo se permiten archivos PDF.
                        </div>
                      </div>
                      <div v-if="uploadingFile" class="mt-2 text-info">
                        <span
                          class="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                          style="width: 0.875rem; height: 0.875rem;"
                        ></span>
                        Subiendo documento...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="row mt-3">
                <div class="col-lg-12 mt-3">
                  <h6>Anexo VI</h6>
                  <b-button
                    variant="info"
                    @click.prevent="
                      downloadFiles(
                        accesion_center?.document_name,
                        accesion_center?.essay?.folder_documents_id
                      )
                    "
                    ><i class="fas fa-file-download mr-2"></i>
                    Descargar</b-button
                  >
                </div>
              </div>
            </div>
          </div>
        </form>
      </template>
      <template #modal-footer="{}">
        <div class="d-flex justify-content-between w-100">
          <b-button
            v-if="
              showElement(
                [3],
                accesion_center?.essay?.essay_status_id != 7,
                accesion_center?.essay?.essay_status_id != 4,
                accesion_center?.essay?.essay_status_id != 6
              )
            "
            variant="primary"
            @click="saveAccession('editando')"
            :disabled="loading || uploadingFile"
            ><span
              class="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
              v-if="loading"
            ></span
            >Guardar adhesión</b-button
          >
          <div>
            <b-button
              v-if="
                showElement(
                  [3],
                  accesion_center?.essay?.essay_status_id != 7,
                  accesion_center?.essay?.essay_status_id != 4,
                  accesion_center?.essay?.essay_status_id != 6
                )
              "
              class="mr-2"
              variant="success"
              @click="addAccession('finalizado')"
              :disabled="loading || uploadingFile"
              ><span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
                v-if="loading"
              ></span
              >Finalizar adhesión</b-button
            >
            <b-button
              v-if="showElement([1])"
              class="mr-2"
              variant="success"
              @click="changeStatusAccession('aceptada')"
              :disabled="loading || uploadingFile"
              ><span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
                v-if="loading && accesion_company.status == 'aceptada'"
              ></span
              >Aceptar</b-button
            >
            <b-button
              v-if="showElement([1])"
              class="mr-2"
              variant="danger"
              @click="changeStatusAccession('rechazada')"
              :disabled="loading || uploadingFile"
              ><span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
                v-if="loading && accesion_company.status == 'rechazada'"
              ></span
              >Rechazar</b-button
            >
            <b-button @click="cancelForm()">Cancel</b-button>
          </div>
        </div>
        >
      </template>
    </b-modal>
  </div>
</template>
